/**
 * FoodPulseGeoNewsPage — state- and country-level food-news landing.
 *
 * Routes:
 *   /food-pulse/state/:country/:state    — e.g. /food-pulse/state/us/or
 *   /food-pulse/country/:country         — e.g. /food-pulse/country/us
 *
 * Renders the same `<FoodNewsFeed>` widget used on the city page but
 * scoped to the wider geography, plus a compact "drill down to a
 * major city" strip so visitors can jump into the city-level feed.
 */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import FoodNewsFeed from "@/components/FoodNewsFeed";
import TalkVibesTab  from "@/components/TalkVibesTab";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const AMBER = "#E4A140";

// Small, curated list of headline cities per state so we can offer a
// "drill down" strip without pulling the full 20k-business directory.
// Extend as coverage grows.
const CITIES_BY_STATE = {
  OR: ["Portland"],
  WA: ["Seattle", "Spokane"],
  CA: ["Los Angeles", "San Francisco", "San Diego", "San Jose"],
  NY: ["New York"],
  GA: ["Atlanta"],
  FL: ["Miami"],
  IL: ["Chicago"],
  TX: ["Austin", "Houston", "Dallas"],
  MA: ["Boston"],
  IN: [],
  KL: [],
};

const STATE_LABELS = {
  OR: "Oregon", WA: "Washington", CA: "California", NY: "New York",
  GA: "Georgia", FL: "Florida", IL: "Illinois", TX: "Texas", MA: "Massachusetts",
};

const COUNTRY_LABELS = {
  US: "United States", IN: "India", CA: "Canada", UK: "United Kingdom",
  AU: "Australia", DE: "Germany", FR: "France", ES: "Spain", IT: "Italy",
};

export default function FoodPulseGeoNewsPage({ scope }) {
  // `scope` is one of "state" | "country" (set via <Route element={<... scope="state" />} />)
  const params = useParams();
  const country = (params.country || "").toUpperCase();
  const state   = scope === "state" ? (params.state || "").toUpperCase() : null;

  const placeLabel = state
    ? `${STATE_LABELS[state] || state}, ${COUNTRY_LABELS[country] || country}`
    : (COUNTRY_LABELS[country] || country);

  const drilldown = useMemo(() => {
    if (state) {
      return (CITIES_BY_STATE[state] || []).map((c) => ({
        label: c,
        href: `/food-pulse/city/${country}/${state}/${encodeURIComponent(c)}`,
      }));
    }
    // Country level — show a mixed list of top cities across covered states.
    const flat = [];
    Object.entries(CITIES_BY_STATE).forEach(([s, cities]) => {
      cities.forEach((c) => flat.push({ label: `${c}, ${s}`, href: `/food-pulse/city/${country}/${s}/${encodeURIComponent(c)}` }));
    });
    return flat.slice(0, 20);
  }, [country, state]);

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="fp-geo-news-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to={state ? `/food-pulse/country/${country}` : "/food-pulse/city"}
              className="inline-flex items-center gap-1 text-xs font-semibold mb-2"
              style={{ color: GREEN }}
              data-testid="geo-news-back">
          <ArrowLeft size={12} /> {state ? `${COUNTRY_LABELS[country] || country} food news` : "All cities"}
        </Link>

        <header className="mb-6">
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: AMBER }}>
            {scope === "state" ? "State food news" : "Country food news"}
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight" style={{ color: GREEN }} data-testid="geo-news-title">
            {placeLabel} · Food News, Recalls & Outbreaks
          </h1>
          <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            Live recalls, outbreak reports, and food-industry news auto-curated
            from Google News, the FDA, CDC, USDA FSIS, EFSA, WHO, and leading
            food-safety publications — updated every 6 hours.
          </p>
        </header>

        <FoodNewsFeed country={country} state={state || undefined} title="" limit={30} />

        {drilldown.length > 0 && (
          <section className="mt-8 rounded-2xl border bg-white p-5" style={{ borderColor: "#E9D8B4" }} data-testid="drilldown-strip">
            <div className="text-sm font-bold mb-3" style={{ color: GREEN }}>
              Drill down to a city
            </div>
            <div className="flex flex-wrap gap-2">
              {drilldown.map((d) => (
                <Link key={d.href} to={d.href}
                      className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold hover:shadow-sm transition"
                      style={{ borderColor: "#E9D8B4", color: GREEN }}
                      data-testid={`drill-${d.label.replace(/\s+/g, "-")}`}>
                  <MapPin size={11} /> {d.label}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8">
          <TalkVibesTab
            entityType={scope}
            entityId={state ? `${country}:${state}` : country}
            subject={placeLabel}
            intro={`Have first-hand insights on food safety in ${placeLabel}? Share a tip, ask a question, or flag a local recall.`}
          />
        </div>
      </div>
    </main>
  );
}
